const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

let accessTokenCache = null;
let refreshTokenCache = null;

const isBrowser = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return !!window.localStorage;
  } catch (error) {
    return false;
  }
};

const getStorage = (storageName) => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window[storageName] || null;
  } catch (error) {
    console.warn(`[token.service] Unable to access ${storageName}`, error);
    return null;
  }
};

const readStorage = (storageName, key) => {
  const storage = getStorage(storageName);
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch (error) {
    console.warn(`[token.service] Unable to read ${key} from ${storageName}`, error);
    return null;
  }
};

const removeStorage = (storageName, key) => {
  const storage = getStorage(storageName);
  if (!storage) {
    return null;
  }
  try {
    storage.removeItem(key);
  } catch (error) {
    console.warn(`[token.service] Unable to remove ${key} from ${storageName}`, error);
  }
};

const readLegacyToken = (key) => {
  const localToken = readStorage("localStorage", key) || "";
  if (localToken) {
    return localToken;
  }
  return readStorage("sessionStorage", key) || "";
};

const purgeLegacyTokenStorage = () => {
  removeStorage("localStorage", ACCESS_TOKEN_KEY);
  removeStorage("localStorage", REFRESH_TOKEN_KEY);
  removeStorage("sessionStorage", ACCESS_TOKEN_KEY);
  removeStorage("sessionStorage", REFRESH_TOKEN_KEY);
};

export const bootstrapLegacyTokenState = () => {
  if (!isBrowser()) {
    if (accessTokenCache === null) {
      accessTokenCache = "";
    }
    if (refreshTokenCache === null) {
      refreshTokenCache = "";
    }
    return;
  }

  if (accessTokenCache === null) {
    accessTokenCache = readLegacyToken(ACCESS_TOKEN_KEY);
  }
  if (refreshTokenCache === null) {
    refreshTokenCache = readLegacyToken(REFRESH_TOKEN_KEY);
  }

  // Always remove persistent legacy auth keys at bootstrap.
  purgeLegacyTokenStorage();
};

export const getAccessToken = () => {
  if (accessTokenCache !== null) {
    return accessTokenCache || null;
  }

  bootstrapLegacyTokenState();
  return accessTokenCache || null;
};

export const getRefreshToken = () => {
  if (refreshTokenCache !== null) {
    return refreshTokenCache || null;
  }

  bootstrapLegacyTokenState();
  return refreshTokenCache || null;
};

export const setAccessToken = (token) => {
  const normalized = typeof token === "string" ? token.trim() : "";
  accessTokenCache = normalized;

  // Always keep tokens in memory only; clear any legacy persisted values.
  purgeLegacyTokenStorage();
};

export const setRefreshToken = (token) => {
  const normalized = typeof token === "string" ? token.trim() : "";
  refreshTokenCache = normalized;

  // Always keep tokens in memory only; clear any legacy persisted values.
  purgeLegacyTokenStorage();
};

export const setTokenPair = ({ accessToken, refreshToken }) => {
  if (accessToken !== undefined) {
    setAccessToken(accessToken);
  }

  if (refreshToken !== undefined) {
    setRefreshToken(refreshToken);
  }
};

export const clearAuthTokens = () => {
  accessTokenCache = "";
  refreshTokenCache = "";
  purgeLegacyTokenStorage();
};

export const hasAccessToken = () => !!getAccessToken();
export const hasRefreshToken = () => !!getRefreshToken();

export const getApiRootUrl = () => {
  const baseUrl = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/+$/, "");

  if (!baseUrl) {
    return "";
  }

  return baseUrl.replace(/\/vue$/, "");
};
